/**
 * B-098 search wall: anonymous "browser sticker".
 *
 * The wall counts anonymous searchers by a random id minted on first
 * visit and stored in a first-party cookie (`mio_session` - the name
 * SearchSecurityMiddleware already looks for). That id is sent to the api
 * as the `x-mio-session` header on every browser search so the gate can
 * count fresh searches per sticker (Option A: sticker only, no IP keying;
 * each browser gets its own free allowance, household-friendly).
 *
 * Browser-only: every function returns null / {} during SSR (no
 * document), so server-rendered page calls send no sticker and are never
 * subject to the per-identity limits.
 */

const COOKIE_NAME = "mio_session";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

function mintId(): string {
  // crypto.randomUUID is available in all secure contexts (https + the
  // localhost dev origin). Fall back to a timestamp+random id only if a
  // very old browser lacks it, so we never throw.
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `s_${crypto.randomUUID()}`;
    }
  } catch {
    // fall through to the non-crypto fallback
  }
  return `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * This browser's anonymous session sticker, minting + persisting one on
 * first call. Returns null on the server (SSR), where there is no browser
 * to attribute a search to.
 */
export function getOrCreateAnonSession(): string | null {
  if (typeof window === "undefined") return null;
  let id = readCookie(COOKIE_NAME);
  if (!id) {
    id = mintId();
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(id)}; path=/; ` +
      `max-age=${MAX_AGE_SECONDS}; SameSite=Lax`;
  }
  return id;
}

/**
 * Identity headers for a browser search call: the Clerk user id (when
 * signed in, resolved server-side and passed down) plus the anonymous
 * sticker. The api counts signed-in users by account (user id takes
 * precedence) and anonymous users by sticker. On the server both are
 * absent so this returns {} - SSR page renders are never per-identity
 * gated.
 */
export function searchIdentityHeaders(
  userId?: string | null,
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (userId) headers["x-mio-user-id"] = userId;
  const sticker = getOrCreateAnonSession();
  if (sticker) headers["x-mio-session"] = sticker;
  return headers;
}
